import type { IntlShape } from 'react-intl';

import { BRAND, WITHDRAWAL_ENABLED } from '@/lib/const';
import { localizedWithdrawalSubmitErrorMessage } from '@/lib/withdrawal/withdrawalSubmitErrorMessage';
import * as PM from '@/messages/payment';
import type { WithdrawalSubmitItemInput } from '@/views/OrderReturnView/orderReturnSubmitItems';

export type WithdrawalSubmitOutcome =
  | { status: 'submitted' }
  | { message: string; status: 'error' };

type GuestWithdrawalSubmitInput = {
  formatMessage: IntlShape['formatMessage'];
  guestAuthContext:
    | {
        orderEmail: string;
        orderNumber: string;
        recaptchaToken: string;
      }
    | null
    | undefined;
  items: WithdrawalSubmitItemInput[];
  orderId: string;
  submitRecaptchaToken: string | null;
};

type RegisteredWithdrawalSubmitInput = {
  formatMessage: IntlShape['formatMessage'];
  items: WithdrawalSubmitItemInput[];
  orderAppOrderId: string;
};

type WithdrawalSubmitApiJson = {
  data?: {
    withdrawalSubmit?: {
      errors?: Array<{ code?: string; message?: string | null }>;
      withdrawalSubmit?: { code?: string | null } | null;
    };
  };
};

function parseWithdrawalSubmitResponse(
  formatMessage: IntlShape['formatMessage'],
  payload: WithdrawalSubmitApiJson,
): WithdrawalSubmitOutcome {
  const generic = () =>
    formatMessage(PM.paymentMessages.withdrawalGenericError);

  const errors = payload.data?.withdrawalSubmit?.errors ?? [];
  const submitCode =
    payload.data?.withdrawalSubmit?.withdrawalSubmit?.code ?? null;

  if (errors.length > 0) {
    return {
      message: localizedWithdrawalSubmitErrorMessage(formatMessage, errors),
      status: 'error',
    };
  }

  if (submitCode === 'OK') {
    return { status: 'submitted' };
  }

  return { message: generic(), status: 'error' };
}

async function postWithdrawalSubmit(
  body: Record<string, unknown>,
): Promise<Response> {
  return fetch('/api/withdrawal/submit', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });
}

export async function submitGuestWithdrawal(
  input: GuestWithdrawalSubmitInput,
): Promise<WithdrawalSubmitOutcome> {
  const generic = () =>
    input.formatMessage(PM.paymentMessages.withdrawalGenericError);

  if (!WITHDRAWAL_ENABLED) {
    return { message: generic(), status: 'error' };
  }

  if (!input.guestAuthContext) {
    return { message: generic(), status: 'error' };
  }

  const response = await postWithdrawalSubmit({
    input: {
      brand: BRAND.toUpperCase(),
      orderId: input.orderId,
      guestContext: {
        email: input.guestAuthContext.orderEmail,
        recaptchaToken: input.submitRecaptchaToken,
      },
      items: input.items,
    },
  });

  if (!response.ok) {
    return { message: generic(), status: 'error' };
  }

  const payload = (await response.json()) as WithdrawalSubmitApiJson;
  return parseWithdrawalSubmitResponse(input.formatMessage, payload);
}

export async function submitRegisteredWithdrawal(
  input: RegisteredWithdrawalSubmitInput,
): Promise<WithdrawalSubmitOutcome> {
  const generic = () =>
    input.formatMessage(PM.paymentMessages.withdrawalGenericError);

  if (!WITHDRAWAL_ENABLED) {
    return { message: generic(), status: 'error' };
  }

  const response = await postWithdrawalSubmit({
    input: {
      brand: BRAND.toUpperCase(),
      orderId: input.orderAppOrderId,
      items: input.items,
    },
  });

  if (!response.ok) {
    return { message: generic(), status: 'error' };
  }

  const payload = (await response.json()) as WithdrawalSubmitApiJson;
  return parseWithdrawalSubmitResponse(input.formatMessage, payload);
}
